/* eslint-disable @typescript-eslint/naming-convention */
import { Component,
    forwardRef,
    Input,
    OnChanges,
    SimpleChanges,
    EventEmitter,
    Output,
    ViewChild,
    ElementRef,
    Renderer2,
    ChangeDetectorRef,
    NgZone,
    AfterViewInit,
    AfterViewChecked,
    OnDestroy} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { DomHandler } from 'primeng/dom';
import { TimeModel } from '../../models/time.model';
import { meridiansEnum } from '../../enums/meridians.enum';
import { TwentyFourHourModel } from '../../models/twenty-four-hour.model';
import { MinuteModel } from '../../models/minute.model';
import { SecondModel } from '../../models/second.model';
import { TwelveHourModel } from '../../models/twelve-hour.model';
import { timeUnitsEnum } from '../../enums/time-units.enum';
import { timeFormatsEnum } from '../../../../Core/Users/enums/time-formats.enum';
import { ActionKeys } from '../../enums/keys.enum';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';

const TimePickerValueAccessor: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TimePickerComponent),
    multi: true
};

export const timePickerComponentText = {
    AM: 'AM',
    PM: 'PM',
    selectTime: 'Select time',
    selectHour: 'Select hour',
    selectMinute: 'Select minute',
    selectSecond: 'Select second',
    selectMeridian: 'Select meridian',
    timeZone: 'Timezone'
};

/**
 * ### Accessibility Usage
 * + ***hourDescribedBy*** - use this to attach error messages to each hour control. Use message's id
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***isHiddenLabel*** - set to true to hide legend from UI. Default is true
 * + ***minuteDescribedBy*** - use this to attach error messages to each minute control. Use message's id
 */
@Component({
    selector: 'agpr-time-picker',
    templateUrl: './time-picker.component.html',
    styleUrls: ['./time-picker.component.scss'],
    animations: [
        trigger('panelState', [
            state('hidden', style({
                opacity: 0
            })),
            state('visible', style({
                opacity: 1
            })),
            transition('visible => hidden', animate('400ms ease-in')),
            transition('hidden => visible', animate('400ms ease-out'))
        ])
    ],
    providers: [TimePickerValueAccessor],
    standalone: false
})
export class TimePickerComponent implements OnChanges, ControlValueAccessor, AfterViewInit, AfterViewChecked, OnDestroy {
    readonly agprComponentStyle = AgprComponentStyleEnum;

    @Input() id: string = null;
    @Input() localization = timePickerComponentText;
    @Input() header: string = null;
    @Input() isHiddenLabel: boolean = true;
    @Input() showTimeZoneSelector: boolean = true;
    @Input() disabled: boolean = false;
    @Input() styleClass: string | object = null;
    @Input() hourDescribedBy: string = null;
    @Input() minuteDescribedBy: string = null;
    @Input() secondDescribedBy: string = null;
    @Input() infoIconStyleClass: string = null;
    @Input() infoTooltipStyleClass: string = null;
    @Input() showTimezoneDisclaimer: boolean = true;
    @Input() durationMode: boolean = false;
    @Input() showHour: boolean = true;
    @Input() showSecond: boolean = false;
    @Input() required: boolean = false;
    @Input() minTime: TimeModel = null;
    @Input() maxTime: TimeModel = null;

    @Output() changeHour: EventEmitter<TwelveHourModel | TwentyFourHourModel> = new EventEmitter<TwelveHourModel | TwentyFourHourModel>();
    @Output() changeMinute: EventEmitter<MinuteModel> = new EventEmitter<MinuteModel>();
    @Output() changeSecond: EventEmitter<SecondModel> = new EventEmitter<MinuteModel>();
    @Output() changeMeridian: EventEmitter<meridiansEnum> = new EventEmitter<meridiansEnum>();
    @Output() changeTimeZone: EventEmitter<string> = new EventEmitter<string>();
    @Output() boundaryReached: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('hourInput', {static: false}) hourInput: ElementRef;
    @ViewChild('minuteInput', {static: false}) minuteInput: ElementRef;
    @ViewChild('secondInput', {static: false}) secondInput: ElementRef;
    @ViewChild('meridianInput', {static: false}) meridianInput: ElementRef;
    @ViewChild('container', {static: false}) containerViewChild: ElementRef;
    @ViewChild('panel', {static: false}) panelViewChild: ElementRef;

    container: HTMLDivElement;
    panel: HTMLDivElement;
    shown: boolean;
    scrollableParents: any[];
    public documentClickListener: any;
    panelVisible: boolean = false;
    selfClick: boolean = false;
    itemClick: boolean = false;
    inputs: ElementRef[];

    value: TimeModel;
    meridians = meridiansEnum;
    timeFormats = timeFormatsEnum;
    minHour: TwelveHourModel | TwentyFourHourModel = 1;
    maxHour: TwelveHourModel | TwentyFourHourModel = 12;
    minMinute: MinuteModel = 0;
    maxMinute: MinuteModel = 59;
    minSecond: SecondModel = 0;
    maxSecond: SecondModel = 59;
    timeUnits = timeUnitsEnum;
    activeTimeUnit: timeUnitsEnum = null;
    keyValue: number = null;

    constructor(public el: ElementRef,
        public renderer: Renderer2,
        private cd: ChangeDetectorRef,
        public zone: NgZone) {}


    private readonly eventListener = () =>  {
        this.hide();
    };

    onFocus() {
        if (!this.panelVisible) {
            this.show();
        }
    }

    onFocusOut(event) {
        if (this.panelVisible
            && event.relatedTarget
            && !this.container.contains(event.relatedTarget)
            && !this.panel.contains(event.relatedTarget)) {
            this.hide();
        }
    }

    ngAfterViewInit() {
        this.container = <HTMLDivElement>this.containerViewChild.nativeElement;
        this.panel = <HTMLDivElement>this.panelViewChild.nativeElement;
        document.body.appendChild(this.panel);
        setTimeout(() => {
            this.inputs = [];
            this.inputs.push(this.hourInput, this.minuteInput, this.secondInput, this.meridianInput);
            this.inputs = this.inputs.filter(input => input !== undefined);
        });
    }

    ngAfterViewChecked() {
        if (this.shown) {
            this.onShow();
            this.shown = false;
        }

        if (this.panelVisible) {
            this.alignPanel();
        }
    }

    alignPanel() {
        DomHandler.absolutePosition(this.panel, this.container);
    }

    /**
     * On changes to the inputs, determines if the time format has changed.
     * If so, updates the hour settings to work for that time format
     */
    ngOnChanges(changes: SimpleChanges): void {
        const timeChanged: boolean = changes.value !== null && changes.value !== undefined;
        const hasCurrentFormat: boolean = (
            timeChanged
            && changes.value.currentValue
            && changes.value.currentValue.format !== null
            && changes.value.currentValue.format !== undefined
        );
        const hasPreviousFormat: boolean = (
            timeChanged
            && changes.value.previousValue
            && changes.value.previousValue.format !== null
            && changes.value.previousValue.format !== undefined
        );
        let currentFormat: timeFormatsEnum = null;
        let previousFormat: timeFormatsEnum = null;
        let formatChanged: boolean = false;

        currentFormat = hasCurrentFormat && timeChanged && changes.value.currentValue.format;
        previousFormat = hasPreviousFormat && timeChanged && changes.value.previousValue.format;
        formatChanged = timeChanged && currentFormat !== previousFormat;

        if (formatChanged && hasCurrentFormat) {
            this.setValidHourRange();
        }
    }

    /**
     * Method required by the ControlValueAccessor
     * to identify when the model has changed
     */
    onModelChange: Function = () => {};

    /**
     * Method required by the ControlValueAccessor
     * to identify when the input has been touched
     */
    onModelTouched: Function = () => {};

    /**
     * Required as part of ControlValueAccessor
     * Adds a new value to the the expression passed to the
     * ngModel property on the component
    */
    writeValue(value: any): void {
        if (value) {
            this.value = value;

            if (value.format === timeFormatsEnum.twentyFourHour) {
                this.setValidHourRange();
            }
        }
    }

    /**
     * Required as part of ControlValueAccessor
     * sets up a way to know when the model has changed
    */
    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    /**
     * Required as part of ControlValueAccessor
     * sets up a way to know when the input has been touched
    */
    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    inputClick(event) {
        if (this.disabled) {
            return;
        }
        this.selfClick = true;
        if (!this.panelVisible) {
            this.show();
        }
    }

    panelClick(event) {
        this.selfClick = true;
    }

    show() {
        if (this.activeTimeUnit === null) {
            const unit = this.hourInput ? this.timeUnits.hours : this.timeUnits.minutes;
            this.setActiveTimeUnit(unit);
        }
        this.panel.style.minWidth = DomHandler.getWidth(this.container) + 'px';
        this.panel.style.zIndex = String(++DomHandler.zindex);
        this.addEventListeners();
        this.panelVisible = true;
        this.shown = true;
    }

    hide() {
        this.activeTimeUnit = null;
        this.removeEventListeners();
        this.panelVisible = false;
    }

    togglePanel() {
        if (this.panelVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    increment(unit: timeUnitsEnum) {
        if (this.isIncrementDisabled(unit)) {
            this.boundaryReached.emit();

            return;
        }
        this.selfClick = true;
        this.setActiveTimeUnit(unit);
        this.alterTime(this.add);
    }

    decrement(unit: timeUnitsEnum) {
        if (this.isDecrementDisabled(unit)) {
            this.boundaryReached.emit();

            return;
        }
        this.selfClick = true;
        this.setActiveTimeUnit(unit);
        this.alterTime(this.subtract);
    }

    isIncrementDisabled(unit: timeUnitsEnum): boolean {
        return this.isArrowDisabled(unit, 1);
    }

    isDecrementDisabled(unit: timeUnitsEnum): boolean {
        return this.isArrowDisabled(unit, -1);
    }

    isMeridianToggleDisabled(): boolean {
        if (!this.value || this.value.format !== timeFormatsEnum.twelveHour || (!this.minTime && !this.maxTime)) {
            return false;
        }
        const targetMeridian = this.value.meridian === meridiansEnum.AM ? meridiansEnum.PM : meridiansEnum.AM;

        return this.isOutOfTimeRange(this.minutesOfDayFor(this.value.format, this.value.hour, this.value.minute, targetMeridian));
    }

    /**
     * Sets the hour to a provided value as long as the value is not too high or too low
    */
    setHour(value: number | string | TwelveHourModel | TwentyFourHourModel): void {
        this.value.hour = null;
        if (value !== null && value !== undefined) {
            requestAnimationFrame(() => {
                this.value.hour = this.getValidTimeUnit<TwelveHourModel | TwentyFourHourModel>(
                    value,
                    this.minHour,
                    this.maxHour
                );
                this.clampToTimeRange();

                this.onModelChange(this.value);
                this.changeHour.emit(this.value.hour);
            });
        }
    }

    /**
     * Set the minute to a provided value as long as the value is not too high or too low
    */
    setMinute(value: number | string | MinuteModel): void {
        this.value.minute = null;
        if (value !== null && value !== undefined) {
            requestAnimationFrame(() => {

                this.value.minute = this.getValidTimeUnit<MinuteModel>(
                    value,
                    this.minMinute,
                    this.maxMinute
                );
                this.clampToTimeRange();
                this.onModelChange(this.value);
                this.changeMinute.emit(this.value.minute);
            });
        }
    }

    /**
     * Set the second to a provided value as long as the value is not too high or too low
    */
    setSecond(value: number | string | SecondModel): void {
        this.value.second = null;
        if (value !== null && value !== undefined) {
            requestAnimationFrame(() => {
                this.value.second = this.getValidTimeUnit<SecondModel>(
                    value,
                    this.minSecond,
                    this.maxSecond
                );
                this.onModelChange(this.value);
                this.changeSecond.emit(this.value.second);
            });
        }
    }

    /**
     * Changes the hour value based on a UI scroll event
    */
    scrollHour(event: WheelEvent): void {
        if (!this.disabled) {
            const time: TimeModel = this.value;

            time.hour = this.getTimeUnitByScrolling<TwelveHourModel | TwentyFourHourModel>(
                event,
                this.value.hour,
                this.minHour,
                this.maxHour
            );
            this.clampToTimeRange();

            this.onModelChange(time);
            this.changeHour.emit(this.value.hour);
        }
    }

    /**
     * Changes the minute value based on a UI scroll event
    */
    scrollMinute(event: WheelEvent): void {
        if (!this.disabled) {
            const time: TimeModel = this.value;

            time.minute = this.getTimeUnitByScrolling<MinuteModel>(
                event,
                this.value.minute,
                this.minMinute,
                this.maxMinute
            );
            this.clampToTimeRange();

            this.onModelChange(time);
            this.changeMinute.emit(this.value.minute);
        }
    }

    /**
     * Changes the second value based on a UI scroll event
    */
    scrollSecond(event: WheelEvent): void {
        const time: TimeModel = this.value;

        time.second = this.getTimeUnitByScrolling<SecondModel>(
            event,
            this.value.second,
            this.minSecond,
            this.maxSecond
        );

        this.onModelChange(time);
        this.changeSecond.emit(this.value.second);
    }

    /**
     * Emits a changeMeridian event with the provided value
    */
    meridianChanged(newValue: meridiansEnum): void {
        this.onModelChange(this.value);
        this.changeMeridian.emit(newValue);
    }

    toggleMeridian() {
        if (this.isMeridianToggleDisabled()) {
            this.boundaryReached.emit();

            return;
        }
        this.selfClick = true;
        if (this.value.meridian === meridiansEnum.AM) {
            this.value.meridian = meridiansEnum.PM;
        } else {
            this.value.meridian = meridiansEnum.AM;
        }
        this.meridianChanged(this.value.meridian);
    }

    /**
     * Sets a unit of time (seconds, minutes or hours) as the active unit
     * This represents the unit the user is focused on
    */
    setActiveTimeUnit(unit: timeUnitsEnum): void {
        if (unit === timeUnitsEnum.seconds) {
            this.secondInput?.nativeElement.select();
        }
        if (unit === timeUnitsEnum.minutes) {
            this.minuteInput?.nativeElement.select();
        }
        if (unit === timeUnitsEnum.hours) {
            this.hourInput?.nativeElement.select();
        }
        if (unit === null) {
            this.meridianInput?.nativeElement.select();
        }
        this.activeTimeUnit = unit;
        this.keyValue = null;
    }

    /**
     * Clears the active unit of any unit of time
     * This represents that the user is not focusing on minutes or hours
    */
    clearActiveTimeUnit(): void {
        this.activeTimeUnit = null;
    }

    /**
     * Fires an event on the component when the time zone is changed
    */
    timeZoneChanged(): void {
        this.onModelChange(this.value);
        this.changeTimeZone.emit(this.value.timeZone);
    }

    inputKeydown(event: KeyboardEvent, input: ElementRef) {
        switch (event.key) {
            case ActionKeys.LEFT:
                this.focusPreviousInput(input);
                break;
            case ActionKeys.RIGHT:
            case ActionKeys.ENTER:
                if (event.altKey && event.key === ActionKeys.ENTER) {
                    this.togglePanel();
                } else if (!this.panelVisible && event.key === ActionKeys.ENTER) {
                    this.show();
                } else {
                    this.focusNextInput(input);
                }
                break;
            case ActionKeys.UP:
                if (event.altKey) {
                    this.togglePanel();
                } else if (this.activeTimeUnit === null) {
                    this.toggleMeridian();
                } else {
                    this.alterTime(this.add);
                }
                event.preventDefault();
                break;
            case ActionKeys.DOWN:
                if (event.altKey) {
                    this.togglePanel();
                } else if (this.activeTimeUnit === null) {
                    this.toggleMeridian();
                } else {
                    this.alterTime(this.subtract);
                }
                event.preventDefault();
                break;
            case '0':
            case '1':
            case '2':
            case '3':
            case '4':
            case '5':
            case '6':
            case '7':
            case '8':
            case '9':
                if (this.activeTimeUnit === null) {
                    break;
                }
                if (this.keyValue === null || this.keyValue > 9) {
                    this.keyValue = Number(event.key);
                } else if (this.keyValue < 10) {
                    this.keyValue = (this.keyValue * 10) + Number(event.key);
                }
                switch (this.activeTimeUnit) {
                    case this.timeUnits.hours:
                        this.setHour(this.keyValue);
                        break;
                    case this.timeUnits.minutes:
                        this.setMinute(this.keyValue);
                        break;
                    case this.timeUnits.seconds:
                        this.setSecond(this.keyValue);
                        break;
                }
                break;
            case 'A':
            case 'a':
                if (this.activeTimeUnit === null) {
                    this.value.meridian = meridiansEnum.AM;
                }
                event.preventDefault();
                break;
            case 'P':
            case 'p':
                if (this.activeTimeUnit === null) {
                    this.value.meridian = meridiansEnum.PM;
                }
                event.preventDefault();
                break;
            case ActionKeys.TAB:
                if (this.isLastInput(input)) {
                    this.hide();
                }
                break;
            case ActionKeys.ESCAPE:
                if (this.panelVisible) {
                    this.hide();
                }
                break;
            case ActionKeys.DELETE:
            case ActionKeys.BACKSPACE:
                switch (this.activeTimeUnit) {
                    case this.timeUnits.hours:
                        this.setHour(null);
                        break;
                    case this.timeUnits.minutes:
                        this.setMinute(null);
                        break;
                    case this.timeUnits.seconds:
                        this.setSecond(null);
                        break;
                }
                event.preventDefault();
                break;
            default:
                event.preventDefault();
                break;
        }
    }

    focusNextInput(input: ElementRef) {
        let index = this.inputs.findIndex(x => x.nativeElement === input);
        if (index === this.inputs.length - 1) {
            this.setFocusedInput(this.inputs[0]);
        } else {
            this.setFocusedInput(this.inputs[++index]);
        }
    }

    focusPreviousInput(input: ElementRef) {
        let index = this.inputs.findIndex(x => x.nativeElement === input);
        if (index === 0) {
            this.setFocusedInput(this.inputs[this.inputs.length - 1]);
        } else {
            this.setFocusedInput(this.inputs[--index]);
        }
    }

    setFocusedInput(input: ElementRef) {
        setTimeout(() => {
            input.nativeElement.focus();
        });
    }

    isLastInput(input: ElementRef): boolean {
        if (!!input && !!this.inputs?.length) {
            return this.inputs.findIndex(x => x.nativeElement === input) === (this.inputs.length - 1);
        }
    }

    /**
     * Alter the time by either adding or subtracting minutes
     * based on the active unit of time
     */
    private alterTime(operation: Function): void {
        const noActiveUnit: boolean = this.activeTimeUnit === null || this.activeTimeUnit === undefined;

        if (operation !== this.add && operation !== this.subtract) {
            console.error('TimePickerComponent: Operation must be add or subtract!');

            return;
        }

        if (this.activeTimeUnit === timeUnitsEnum.hours) {
            const newHour: number = operation(this.value.hour, 1);
            this.setHour(newHour);
        } else if (this.activeTimeUnit === timeUnitsEnum.minutes) {
            const newMinute: number = operation(this.value.minute, 1);
            this.setMinute(newMinute);
        } else if (this.activeTimeUnit === timeUnitsEnum.seconds) {
            const newSecond: number = operation(this.value.second, 1);
            this.setSecond(newSecond);
        }
    }

    /**
     * Adds a and b together, and returns the result
     */
    private add(a: number, b: number): number {
        const result: number = a + b;

        return result;
    }

    /**
     * Subtracts b from a, and returns the result
     */
    private subtract(a: number, b: number): number {
        const result: number = a - b;

        return result;
    }

    /**
     * Sets the maximum allowed hour to either 12 or 24 depending on
     * the current time format
    */
    private setValidHourRange(): void {
        const hasValue: boolean = this.value !== null && this.value !== undefined;
        const hasMeridian: boolean = hasValue && this.value.meridian !== null && this.value.meridian !== undefined;

        if (hasValue) {
            if (this.value.format === timeFormatsEnum.twelveHour) {
                this.minHour = 1;
                this.maxHour = 12;
                if (!hasMeridian) {
                    this.value.meridian = meridiansEnum.AM;
                }
            } else if (this.value.format === timeFormatsEnum.twentyFourHour) {
                this.minHour = 0;
                this.maxHour = 23;
                this.value.meridian = null;
            }
        }
    }

    private isArrowDisabled(unit: timeUnitsEnum, delta: number): boolean {
        if (!this.value || (!this.minTime && !this.maxTime)) {
            return false;
        }

        let hour: number = this.value.hour;
        let minute: number = this.value.minute;
        if (unit === timeUnitsEnum.hours) {
            hour = this.wrapValue(hour + delta, this.minHour, this.maxHour);
        } else if (unit === timeUnitsEnum.minutes) {
            minute = this.wrapValue(minute + delta, this.minMinute, this.maxMinute);
        } else {
            return false;
        }

        return this.isOutOfTimeRange(this.minutesOfDayFor(this.value.format, hour, minute, this.value.meridian));
    }

    private wrapValue(value: number, min: number, max: number): number {
        if (value < min) {
            return max;
        }
        if (value > max) {
            return min;
        }

        return value;
    }

    private isOutOfTimeRange(minutesOfDay: number): boolean {
        if (this.minTime && minutesOfDay < this.minutesOfDay(this.minTime)) {
            return true;
        }
        if (this.maxTime && minutesOfDay > this.minutesOfDay(this.maxTime)) {
            return true;
        }

        return false;
    }

    private minutesOfDay(time: TimeModel): number {
        return this.minutesOfDayFor(time.format, time.hour, time.minute, time.meridian);
    }

    private minutesOfDayFor(format: timeFormatsEnum, hour: number, minute: number, meridian: meridiansEnum): number {
        let hour24 = hour;
        if (format === timeFormatsEnum.twelveHour) {
            if (meridian === meridiansEnum.PM) {
                hour24 = hour === 12 ? 12 : hour + 12;
            } else {
                hour24 = hour === 12 ? 0 : hour;
            }
        }

        return (hour24 * 60) + minute;
    }

    private clampToTimeRange(): void {
        if (!this.value || this.value.hour === null || this.value.minute === null || (!this.minTime && !this.maxTime)) {
            return;
        }

        const minutesOfDay = this.minutesOfDay(this.value);
        if (this.minTime && minutesOfDay < this.minutesOfDay(this.minTime)) {
            this.applyMinutesOfDay(this.minutesOfDay(this.minTime));
        } else if (this.maxTime && minutesOfDay > this.minutesOfDay(this.maxTime)) {
            this.applyMinutesOfDay(this.minutesOfDay(this.maxTime));
        }
    }

    private applyMinutesOfDay(totalMinutes: number): void {
        const hour24 = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;

        if (this.value.format === timeFormatsEnum.twelveHour) {
            this.value.meridian = hour24 >= 12 ? meridiansEnum.PM : meridiansEnum.AM;
            const hour12 = hour24 % 12;
            this.value.hour = (hour12 === 0 ? 12 : hour12) as TwelveHourModel;
        } else {
            this.value.hour = hour24 as TwentyFourHourModel;
        }
        this.value.minute = minute as MinuteModel;
    }

    /**
     * Gets a new unit of time based on the allowed minimum and maximum values
     * and ensures that the returned value is a number
    */
    private getValidTimeUnit<TimeUnit extends number>(
        value: number | string | TimeUnit,
        min: TimeUnit,
        max: TimeUnit
    ): TimeUnit {
        const hasValue: boolean = value !== null && value !== undefined;
        let isTooLow: boolean = false;
        let isTooHigh: boolean = false;

        value = +(+value).toFixed();

        isTooLow = hasValue && value < min;
        isTooHigh = hasValue && value > max;
        if (isTooLow || isNaN(value)) {
            value = max;
        } else if (isTooHigh) {
            value = min;
        }

        return <TimeUnit>value;
    }

    /**
     * Gets a unit of time within the allowed range based on a UI scroll event
    */
    private getTimeUnitByScrolling<TimeUnit extends number>(
        event: WheelEvent,
        value: TimeUnit,
        min: TimeUnit,
        max: TimeUnit
    ): TimeUnit {
        const isScrollUp: boolean = event.deltaY < 0;
        const isScrollDown: boolean = event.deltaY > 0;
        const canIncrease: boolean = value < max;
        const canDecrease: boolean = value > min;

        if (isScrollDown) {
            if (canDecrease) {
                value--;
            } else {
                value = max;
            }
        } else if (isScrollUp) {
            if (canIncrease) {
                value++;
            } else {
                value = min;
            }

        }

        event.preventDefault();

        return value;
    }

    onShow() {
        this.bindDocumentClickListener();

        this.alignPanel();
    }

    addEventListeners() {
        this.scrollableParents = DomHandler.getScrollableParents(this.el.nativeElement);

        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.addEventListener('scroll', this.eventListener);
            });
        }
        window.addEventListener('resize', this.eventListener);
    }

    removeEventListeners() {
        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.removeEventListener('scroll', this.eventListener);
            });
        }

        window.removeEventListener('resize', this.eventListener);
    }

    /**
     * Closes panel on clicks outside the dropdown.
     */
    bindDocumentClickListener() {
        if (!this.documentClickListener) {
            this.documentClickListener = this.renderer.listen('document', 'click', () => {
                if (!this.selfClick) {
                    this.hide();
                    this.unbindDocumentClickListener();
                }
                this.selfClick = false;
                this.cd.markForCheck();
            });
        }
    }

    unbindDocumentClickListener() {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    ngOnDestroy() {
        this.unbindDocumentClickListener();
        this.removeEventListeners();

        if (this.panel) {
            this.panel.parentNode.removeChild(this.panel);
        }
    }
}
